import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { create } from 'zustand';
import { Project, ProjectState } from '../types/project';
import { projectApi } from '../api/projectApi';

/* eslint-disable react-refresh/only-export-components -- file exports provider + hooks + store */
// Simplified ProjectState interface - only what's needed
export interface ExtendedProjectState extends ProjectState {
  refreshProjects: () => Promise<void>;
  refreshProject?: () => Promise<void>;
  refreshProjectSchedules?: (projectId: string) => Promise<void>;
  projectId?: string;
}

// Export as ProjectContextState for backward compatibility
export type ProjectContextState = ExtendedProjectState;

// Create Zustand store for projects list only
export const useProjectStore = create<ExtendedProjectState>()(
  (set, get) => ({
    currentProject: null,
    currentSchedule: null,
    projects: [],
    schedules: [],
    organizations: [],
    loading: false,
    error: null,
    
    // Actions
    setCurrentProject: (project) => set({ currentProject: project }),
    setCurrentSchedule: (schedule) => set({ currentSchedule: schedule }),
    setProjects: (projects) => set({ projects }),
    setSchedules: (schedules) => set({ schedules }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    addProject: (project) => {
      const { projects } = get();
      set({ projects: [...projects, project] });
    },
    
    // API refresh function - only for projects list
    refreshProjects: async () => {
      set({ loading: true, error: null });
      try {
        const fetchedProjects = await projectApi.getProjects();
        set({ projects: fetchedProjects, loading: false });
      } catch (err) {
        console.error('Error fetching projects:', err);
        set({ error: 'Failed to fetch projects', loading: false });
      }
    },
    // Refresh current project
    refreshProject: async () => {
      const { currentProject } = get();
      if (!currentProject) return;
      set({ loading: true, error: null });
      try {
        const updatedProject = await projectApi.getProject(currentProject.id);
        set({ currentProject: updatedProject, loading: false });
        // Also update in projects array
        const { projects } = get();
        set({ projects: projects.map(p => p.id === updatedProject.id ? updatedProject : p) });
      } catch (err) {
        console.error('Error refreshing project:', err);
        set({ error: 'Failed to refresh project', loading: false });
      }
    },
    // Refresh project schedules
    refreshProjectSchedules: async (projectId: string) => {
      try {
        const schedules = await projectApi.getProjectSchedules(projectId);
        set({ schedules });
        // Update current project if it matches
        const { currentProject } = get();
        if (currentProject && currentProject.id === projectId) {
          set({ currentProject: { ...currentProject, schedules } });
        }
      } catch (err) {
        console.error('Error refreshing project schedules:', err);
        set({ error: 'Failed to refresh project schedules' });
      }
    }
  })
);

// For legacy components or when React context is needed
const ProjectContext = createContext<ExtendedProjectState | null>(null);

interface ProjectContextProviderProps {
  children: ReactNode;
}

export const ProjectContextProvider: React.FC<ProjectContextProviderProps> = ({ children }) => {
  // No initialization logic needed - ProjectLoader handles this
  return (
    <ProjectContext.Provider value={useProjectStore()}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjectContext must be used within a ProjectContextProvider');
  }
  return context;
};

export const useProject = useProjectContext; 